import React, {
    createContext,
    Dispatch,
    FunctionComponent,
    ReactNode,
    useContext,
    useEffect,
    useReducer,
    useRef,
} from 'react';

import axios from 'axios';
import { cloneDeep } from 'lodash';

export enum JobState {
    Waiting = 1,
    Running = 2,
    Success = 3,
    Failed = 4,
}

export enum JobNotify {
    None = 1,
    Manual = 2,
    Auto = 3,
}

export interface Job {
    id: string;
    routingKey: string;
    jobState: JobState;
    request: string;
    response: string;
    batch: boolean;
    failures: number;
}

export interface PollJob {
    jobId: string;
    jobResponse: string | object;
    jobState: JobState;
}

export interface JobOptions {
    name?: string;
    notify?: JobNotify;
    notifyThreshold?: number;
}

export interface RunningJob {
    tenantId: number;
    jobId: string;
    name: string;
    options: JobOptions;
    start: number;
    wait: boolean;
    showNotification: boolean;
    promise: Promise<PollJob>;
    resolve: (value: PollJob | PromiseLike<PollJob>) => void;
    reject: (reason?: any) => void;
    timeoutId: number | undefined;
}

export interface JobsContainer {
    runningJobs: {
        [index: string]: RunningJob;
    };
    count: number;
}

export enum JobsActionType {
    Start,
    Poll,
    ShowNotification,
    HideNotification,
    Complete,
}

export interface JobsAction {
    type: JobsActionType;
    jobId?: string;
    runningJob?: RunningJob;
    timeoutId?: number;
}

interface JobsProviderProps {
    children: ReactNode;
}

export interface JobsDispatch {
    handleJob: (tenantId: number, jobId: string, options?: JobOptions) => Promise<PollJob>;
    batchJob: (jobId: string) => void;
    waitJob: (jobId: string) => void;
}

export const getJob = async (tenantId: number, jobId: string): Promise<Job[]> => {
    const { data } = await axios.get<Job[]>(`/api/tenants/${tenantId}/jobs/${jobId}`);

    return data;
};

export const saveJob = async (tenantId: number, job: Job): Promise<Job> => {
    const { data } = await axios.post<Job>(`/api/tenants/${tenantId}/jobs`, { job });

    return data;
};

export const pollJob = async (tenantId: number, jobId: string): Promise<PollJob> => {
    const { data } = await axios.put<PollJob>(`/api/tenants/${tenantId}/jobs/${jobId}/poll`, {
        jobId,
    });

    return data;
};

export const batchJob = async (tenantId: number, jobId: string): Promise<Job> => {
    const { data } = await axios.put<Job>(`/api/tenants/${tenantId}/jobs/${jobId}/batch`, {
        jobId,
    });

    return data;
};

const JobsStateContext = createContext<JobsContainer | undefined>(undefined);
const JobsDispatchContext = createContext<Dispatch<JobsAction> | undefined>(undefined);

const jobsReducer = (jobsContainer: JobsContainer, action: JobsAction): JobsContainer => {
    switch (action.type) {
        case JobsActionType.Start: {
            const updatedJobsContainer = cloneDeep(jobsContainer);

            updatedJobsContainer.count += 1;
            updatedJobsContainer.runningJobs[action.runningJob!.jobId] = action.runningJob!;

            return updatedJobsContainer;
        }

        case JobsActionType.Poll: {
            const updatedJobsContainer = cloneDeep(jobsContainer);

            if (updatedJobsContainer.runningJobs[action.jobId!]) {
                updatedJobsContainer.runningJobs[action.jobId!].timeoutId = action.timeoutId!;
            }

            return updatedJobsContainer;
        }

        case JobsActionType.ShowNotification: {
            const updatedJobsContainer = cloneDeep(jobsContainer);

            if (updatedJobsContainer.runningJobs[action.jobId!]) {
                updatedJobsContainer.runningJobs[action.jobId!].showNotification = true;
            }

            return updatedJobsContainer;
        }

        case JobsActionType.HideNotification: {
            const updatedJobsContainer = cloneDeep(jobsContainer);

            if (updatedJobsContainer.runningJobs[action.jobId!]) {
                updatedJobsContainer.runningJobs[action.jobId!].showNotification = false;
                updatedJobsContainer.runningJobs[action.jobId!].wait = true;
            }

            return updatedJobsContainer;
        }

        case JobsActionType.Complete: {
            const updatedJobsContainer = cloneDeep(jobsContainer);

            if (updatedJobsContainer.runningJobs[action.jobId!]) {
                window.clearTimeout(updatedJobsContainer.runningJobs[action.jobId!].timeoutId!);
                delete updatedJobsContainer.runningJobs[action.jobId!];
            }

            return updatedJobsContainer;
        }

        default: {
            throw new Error(`Invalid action type: ${action.type}`);
        }
    }
};

export const JobsProvider: FunctionComponent<JobsProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(jobsReducer, { runningJobs: {}, count: 1 });

    return (
        <JobsStateContext.Provider value={state}>
            <JobsDispatchContext.Provider value={dispatch}>{children}</JobsDispatchContext.Provider>
        </JobsStateContext.Provider>
    );
};

const useJobsState = (): JobsContainer => {
    const context = useContext(JobsStateContext);

    if (context === undefined) {
        throw new Error('useJobsState must be used within a JobsProvider');
    }

    return context;
};

const useJobsDispatch = (): Dispatch<JobsAction> => {
    const context = useContext(JobsDispatchContext);

    if (context === undefined) {
        throw new Error('useJobsDispatch must be used within a JobsProvider');
    }

    return context;
};

export const useJobs = (): [JobsContainer, JobsDispatch] => {
    const state = useJobsState();
    const dispatch = useJobsDispatch();
    const stateRef = useRef(state);

    const POLL_INTERVAL = 1000; // Poll for job status every sec
    const DEFAULT_NOTIFY_THRESHOLD = 15000; // Manual/Auto notify after 15 secs

    useEffect(() => {
        // This ref is used in the polling functions to access the latest state.
        // Using the state directly returns stale data as it is updated asynchronously by useReducer's dispatch
        stateRef.current = state;
    }, [state]);

    const poll = async (dispatch: Dispatch<JobsAction>, jobId: string): Promise<void> => {
        const runningJob = stateRef.current.runningJobs[jobId];

        if (!runningJob) {
            return;
        }

        try {
            const job = await pollJob(runningJob.tenantId, runningJob.jobId);

            if (job.jobState === JobState.Success || job.jobState === JobState.Failed) {
                // Completed
                try {
                    let response;

                    try {
                        response =
                            job.jobResponse && typeof job.jobResponse === 'string' ? JSON.parse(job.jobResponse) : null;
                    } catch (e) {
                        response = job.jobResponse;
                    }
                    runningJob.resolve({
                        jobId: runningJob.jobId,
                        jobResponse: response,
                        jobState: job.jobState,
                    });
                } catch (error) {
                    runningJob.reject(error);
                }

                completeRunningJob(dispatch, runningJob.jobId);
            } else if (runningJob.options.notify === JobNotify.None) {
                // Not Completed

                // Keep polling
                pollRunningJob(dispatch, jobId, false);
            } else if (Date.now() - runningJob.start > runningJob.options.notifyThreshold! && !runningJob.wait) {
                // Over notify threshold and user has NOT indicated to wait
                if (runningJob.options.notify === JobNotify.Manual) {
                    // Ask User
                    if (!runningJob.showNotification) {
                        // to make sure that the modal doesn't reopen
                        dispatch({
                            type: JobsActionType.ShowNotification,
                            jobId,
                        });
                    }

                    // Keep polling
                    pollRunningJob(dispatch, jobId, false);
                } else if (runningJob.options.notify === JobNotify.Auto) {
                    // Auto
                    batchRunningJob(dispatch, runningJob.jobId);
                }
            } else {
                // Keep polling
                pollRunningJob(dispatch, jobId, false);
            }
        } catch (error) {
            runningJob.reject(error);
            completeRunningJob(dispatch, runningJob.jobId);
        }
    };

    const pollRunningJob = (dispatch: Dispatch<JobsAction>, jobId: string, initial: boolean): void => {
        const interval = POLL_INTERVAL;

        const timeoutId = window.setTimeout(() => {
            poll(dispatch, jobId);
        }, interval);

        dispatch({
            type: JobsActionType.Poll,
            jobId,
            timeoutId,
        });
    };

    const batchRunningJob = (dispatch: Dispatch<JobsAction>, jobId: string): void => {
        const runningJob = stateRef.current?.runningJobs[jobId];

        if (!runningJob) {
            return;
        }

        batchJob(runningJob.tenantId, runningJob.jobId)
            .then((job) => {
                runningJob.resolve({
                    jobId: job.id,
                    jobResponse: job.response,
                    jobState: job.jobState,
                });

                completeRunningJob(dispatch, jobId);
            })
            .catch((error) => {
                runningJob.reject(error);
                completeRunningJob(dispatch, jobId);
            });
    };

    const completeRunningJob = (dispatch: Dispatch<JobsAction>, jobId: string): void => {
        dispatch({
            type: JobsActionType.Complete,
            jobId,
        });
    };

    const waitRunningJob = (dispatch: Dispatch<JobsAction>, jobId: string): void => {
        dispatch({
            type: JobsActionType.HideNotification,
            jobId,
        });
    };

    const jobsDispatch: JobsDispatch = {
        handleJob: (tenantId: number, jobId: string, options: JobOptions = {}): Promise<PollJob> => {
            const { runningJobs, count } = stateRef.current;

            // Not already being handled
            if (!runningJobs[jobId]) {
                const defaultOptions: JobOptions = {
                    name: `Job ${count}`,
                    notify: JobNotify.Manual,
                    notifyThreshold: DEFAULT_NOTIFY_THRESHOLD,
                };

                let promiseResolve: (value: PollJob | PromiseLike<PollJob>) => void;
                let promiseReject: (reason?: any) => void;

                const promise = new Promise<PollJob>((resolve, reject) => {
                    promiseResolve = resolve;
                    promiseReject = reject;
                });

                const jobOptions = Object.assign(defaultOptions, options);

                const runningJob = {
                    tenantId,
                    jobId,
                    name: jobOptions.name!,
                    options: jobOptions,
                    start: Date.now(),
                    wait: false,
                    showNotification: false,
                    promise,
                    resolve: promiseResolve!,
                    reject: promiseReject!,
                    timeoutId: undefined,
                };

                dispatch({
                    type: JobsActionType.Start,
                    jobId,
                    runningJob,
                });

                pollRunningJob(dispatch, jobId, true);

                return promise;
            }

            return runningJobs[jobId].promise;
        },
        batchJob: (jobId: string): void => {
            batchRunningJob(dispatch, jobId);
        },
        waitJob: (jobId: string): void => {
            waitRunningJob(dispatch, jobId);
        },
    };

    return [state, jobsDispatch];
};
